import { type FC, useState } from 'react';
import Icon from '@/components/UI/Icon';
import Spinner from '@/components/UI/Spinner/Spinner';
import { errorToast } from '@/helpers';
import { ApiService, ResponseType } from '@/support/ApiService';
import { _notNil } from '@/littledash';
import type { components } from '@/generated/internal-api/openapi-types';

type BenchlingEntityType = components['schemas']['BenchlingEntityType'];

interface BenchlingEntityLinkProps {
  entityType: BenchlingEntityType;
  apiId: string;
  benchlingEntityWebUrl?: string | null;
  visible?: boolean;
  testId?: string;
}

/**
 * Renders a clickable Benchling (JeffyP) icon that opens the entity's
 * Benchling web URL in a new tab. If no URL is provided, it performs
 * a lookup via the API before navigating.
 */
const BenchlingEntityLink: FC<BenchlingEntityLinkProps> = ({
  entityType,
  apiId,
  benchlingEntityWebUrl,
  visible = true,
  testId = 'benchling-entity-link',
}) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!visible) {
    return null;
  }

  const handleClick = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    if (_notNil(benchlingEntityWebUrl)) {
      window.open(benchlingEntityWebUrl, '_blank', 'noopener noreferrer');
      return;
    }

    setIsLoading(true);
    try {
      const response = await ApiService.call({
        endpoint: 'POST /api/v1/benchling-entity-link-lookup',
        body: { entity_type: entityType, api_id: apiId },
        options: {
          onError: {
            slug: 'benchling-entity-link-lookup',
            toast_message: 'Failed to look up Benchling link. Please try again.',
          },
        },
      });

      if (response.type === ResponseType.Success && _notNil(response.body?.benchling_entity_web_url)) {
        window.open(response.body.benchling_entity_web_url, '_blank', 'noopener noreferrer');
      } else {
        errorToast('Unable to find Benchling link for this entity.');
      }
    } catch {
      // ApiService handles toast + capture via onError options
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <span className="dib mr1" style={{ minWidth: '40px', paddingTop: '3px' }}>
        <Spinner size="small" color="blue" />
      </span>
    );
  }

  return (
    <Icon
      className="blue dib mr1 pointer"
      onClick={handleClick}
      style={{ minWidth: '40px', paddingTop: '3px' }}
      icon="jeffy_p_circle"
      tooltip="Click to view in Benchling"
      width="24"
      height="24"
      viewBox="0 0 20 20"
      testId={testId}
    />
  );
};

export default BenchlingEntityLink;
